const WishlistStore = {
  key: 'merchant_wishlist',

  isSame(first, second) {
    if (!first || !second || first.type !== second.type) return false;
    return first.handle === second.handle;
  },

  normalizeArticleHandle(handle, url = '') {
    const normalizedHandle = String(handle);
    if (normalizedHandle.startsWith('article-') || normalizedHandle.startsWith('recipe-')) return normalizedHandle;

    return String(url).includes('/blogs/recipes/') ? `recipe-${normalizedHandle}` : `article-${normalizedHandle}`;
  },

  normalize(entry) {
    if (!entry || !['product', 'article'].includes(entry.type) || !entry.handle) return null;

    const url = String(entry.url || (entry.type === 'product' ? `/products/${entry.handle}` : ''));
    const handle = entry.type === 'article' ? this.normalizeArticleHandle(entry.handle, url) : String(entry.handle);

    return {
      type: entry.type,
      handle,
      url,
    };
  },

  getAll() {
    let storedEntries = [];

    try {
      storedEntries = JSON.parse(localStorage.getItem(this.key) || '[]');
    } catch (error) {
      console.error('Unable to read wishlist data.', error);
    }

    if (!Array.isArray(storedEntries)) storedEntries = [];

    const entries = storedEntries.map((entry) => this.normalize(entry)).filter(Boolean);
    const uniqueEntries = entries.filter(
      (entry, index) => entries.findIndex((candidate) => this.isSame(candidate, entry)) === index
    );

    if (JSON.stringify(storedEntries) !== JSON.stringify(uniqueEntries)) this.save(uniqueEntries);
    return uniqueEntries;
  },

  save(entries) {
    localStorage.setItem(this.key, JSON.stringify(entries));
    this.updateCount(entries.length);
  },

  has(entry) {
    return this.getAll().some((item) => this.isSame(item, entry));
  },

  toggle(entry) {
    const entries = this.getAll();
    const index = entries.findIndex((item) => this.isSame(item, entry));
    const added = index === -1;

    if (added) {
      entries.push(entry);
    } else {
      entries.splice(index, 1);
    }

    this.save(entries);
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { entry, added, entries } }));
    return added;
  },

  updateCount(count = this.getAll().length, animate = false) {
    document.querySelectorAll('.header__icon--wishlist-count').forEach((element) => {
      element.textContent = count > 0 ? String(count) : '';
      if (!animate) return;

      element.classList.remove('active');
      void element.offsetWidth;
      element.classList.add('active');
      setTimeout(() => element.classList.remove('active'), 1500);
    });
  },
};

class ProductWishlist extends HTMLElement {
  connectedCallback() {
    if (this.dataset.wishlistInitialized === 'true') return;
    this.dataset.wishlistInitialized = 'true';
    this.setAttribute('role', 'button');

    this.entry = this.getEntry();
    this.syncState();
    this.addEventListener('click', this.onToggle);
    this.addEventListener('keydown', this.onKeyDown);
  }

  getEntry() {
    const type = this.dataset.wishlistType;
    const handle = this.dataset.wishlistHandle;
    const fallbackUrl = type === 'product' ? `/products/${handle}` : '';

    return WishlistStore.normalize({
      type,
      handle,
      url: this.dataset.wishlistUrl || fallbackUrl,
    });
  }

  syncState() {
    if (!this.entry) return;
    const isActive = WishlistStore.has(this.entry);
    this.classList.toggle('active', isActive);
    this.setAttribute('aria-pressed', String(isActive));
  }

  onToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!this.entry) return;

    WishlistStore.toggle(this.entry);
    document.querySelectorAll('icon-wishlist').forEach((button) => button.syncState?.());
    WishlistStore.updateCount(undefined, true);
  };

  onKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.onToggle(event);
  };
}

class WishlistGrid extends HTMLElement {
  connectedCallback() {
    this.lists = {
      product: this.querySelector('[data-wishlist-list="product"]'),
      recipe: this.querySelector('[data-wishlist-list="recipe"]'),
      education: this.querySelector('[data-wishlist-list="education"]'),
    };
    this.emptyState = this.querySelector('[data-wishlist-empty]');
    this.loadingState = this.querySelector('[data-wishlist-loading]');
    this.renderVersion = 0;
    this.render();
    document.addEventListener('wishlist:change', this.onWishlistChange);
  }

  disconnectedCallback() {
    document.removeEventListener('wishlist:change', this.onWishlistChange);
  }

  onWishlistChange = (event) => {
    if (!event.detail.added) {
      this.renderVersion += 1;
      this.querySelectorAll('[data-wishlist-list] icon-wishlist').forEach((button) => {
        const buttonEntry = button.getEntry?.();
        if (WishlistStore.isSame(buttonEntry, event.detail.entry)) {
          button.closest('.grid__item, .wishlist-grid__article-item')?.remove();
        }
      });
      this.updateState();
      return;
    }

    this.render();
  };

  async render() {
    const renderVersion = ++this.renderVersion;
    const entries = WishlistStore.getAll();
    Object.values(this.lists).forEach((list) => {
      if (list) list.innerHTML = '';
    });
    this.loadingState?.classList.toggle('hidden', entries.length === 0);

    const cards = await Promise.all(entries.map((entry) => this.fetchCard(entry)));
    if (renderVersion !== this.renderVersion) return;

    cards.forEach(({ destination, markup }) => {
      if (markup) this.lists[destination]?.insertAdjacentHTML('beforeend', markup);
    });

    this.loadingState?.classList.add('hidden');
    this.updateState();
  }

  async fetchCard(entry) {
    if (!entry.url) return { destination: entry.type, markup: '' };

    const sectionId = entry.type === 'article' ? 'wishlist-article-card' : 'wishlist-product-card';

    try {
      const requestUrl = this.getLocalizedUrl(entry.url);
      if (requestUrl.origin !== window.location.origin) return { destination: entry.type, markup: '' };
      requestUrl.searchParams.set('section_id', sectionId);

      const response = await fetch(requestUrl);
      if (!response.ok) return { destination: entry.type, markup: '' };

      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const cardSelector = entry.type === 'article' ? '.wishlist-grid__article-item' : '.grid__item';
      const card = documentFragment.querySelector(cardSelector);
      const destination = entry.type === 'article' ? card?.dataset.wishlistCategory : entry.type;
      return { destination, markup: card?.outerHTML || '' };
    } catch (error) {
      console.error(`Unable to load wishlist ${entry.type} "${entry.handle}".`, error);
      return { destination: entry.type, markup: '' };
    }
  }

  getLocalizedUrl(url) {
    const requestUrl = new URL(url, window.location.origin);
    const rootUrl = window.routes?.root_url || '/';
    const resourcePath = this.getResourcePath(requestUrl.pathname);

    if (resourcePath) {
      requestUrl.pathname = `${rootUrl.replace(/\/?$/, '/')}${resourcePath.replace(/^\//, '')}`;
    }

    return requestUrl;
  }

  getResourcePath(pathname) {
    const resourceMarkers = ['/blogs/', '/products/'];
    const markerIndex = resourceMarkers
      .map((marker) => pathname.indexOf(marker))
      .filter((index) => index !== -1)
      .sort((first, second) => first - second)[0];

    return markerIndex === undefined ? null : pathname.slice(markerIndex);
  }

  updateState() {
    let hasItems = false;

    Object.entries(this.lists).forEach(([type, list]) => {
      const groupHasItems = Boolean(list?.children.length);
      hasItems ||= groupHasItems;
      list?.closest(`[data-wishlist-group="${type}"]`)?.classList.toggle('hidden', !groupHasItems);
    });

    this.emptyState?.classList.toggle('hidden', hasItems);
    this.closest('.section-wishlist')?.classList.remove('wishlist-initial-hidden');
    this.closest('.section-wishlist')?.classList.add('wishlist-initial-visible');
  }
}

if (!customElements.get('icon-wishlist')) customElements.define('icon-wishlist', ProductWishlist);
if (!customElements.get('wishlist-grid')) customElements.define('wishlist-grid', WishlistGrid);
WishlistStore.updateCount();
